import React, { useEffect, useMemo, useState } from "react";
import { usePlanner } from "../../../contexts/PlannerContext";
import { useStaff } from "../../../contexts/StaffContext";
import TaskEditorModal from "./TaskEditorModal";
import NewTaskModal from "../NewTaskModal";

/* ---------- Date helpers ---------- */
const startOfDay   = (d)=> new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay     = (d)=> new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23,59,59,999);
const addDays      = (d,n)=> new Date(d.getFullYear(), d.getMonth(), d.getDate()+n);
const addMonths    = (d,n)=> new Date(d.getFullYear(), d.getMonth()+n, 1);
const addYears     = (d,n)=> new Date(d.getFullYear()+n, 0, 1);
const startOfWeek  = (d)=> { const s=new Date(d); const day=(s.getDay()+6)%7; s.setDate(s.getDate()-day); s.setHours(0,0,0,0); return s; };
const startOfMonth = (d)=> new Date(d.getFullYear(), d.getMonth(), 1);
const startOfYear  = (d)=> new Date(d.getFullYear(), 0, 1);

function formatDate(d, opts) { try { return d.toLocaleDateString(undefined, opts); } catch { return ""; } }
function timeRange(a,b){
  try{
    const s=new Date(a).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"});
    const e=new Date(b).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"});
    return `${s} – ${e}`;
  }catch{return ""; }
}
function overlaps(ev, from, to){
  const s=new Date(ev.start).getTime(), e=new Date(ev.end).getTime();
  return e>=from.getTime() && s<=to.getTime();
}
function stageTag(ev){
  const t=(ev.tags||[]).find(x=>["forming","drying","bisque","glazing","glazeFiring","qcPack","dispatch"].includes(x));
  return t || "other";
}
function rgba(hex, a){
  try{
    const c=String(hex||"#888").replace("#","").padEnd(6,"8");
    const r=parseInt(c.slice(0,2),16), g=parseInt(c.slice(2,4),16), b=parseInt(c.slice(4,6),16);
    return `rgba(${r},${g},${b},${a})`;
  }catch{return "rgba(255,255,255,.18)";}
}
function moveToDatePreserveTime(isoStart, isoEnd, targetDate){
  const s = new Date(isoStart), e = new Date(isoEnd);
  const dur = e.getTime() - s.getTime();
  const base = new Date(targetDate);
  const nextStart = new Date(base.getFullYear(), base.getMonth(), base.getDate(), s.getHours(), s.getMinutes(), s.getSeconds(), s.getMilliseconds());
  const nextEnd = new Date(nextStart.getTime() + dur);
  return { start: nextStart.toISOString(), end: nextEnd.toISOString() };
}
function buildQuickEvent(atDate){
  const d = new Date(atDate);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0, 0, 0);
  const end   = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 10, 0, 0, 0);
  return {
    id: crypto?.randomUUID ? crypto.randomUUID() : `tmp-${Date.now()}`,
    title: "",
    start: start.toISOString(),
    end: end.toISOString(),
    status: "todo",
    progress: 0,
    category: "",
    assigneeId: "",
    color: "#3b82f6",
    tags: [],
  };
}

/* ---------- Assignee helpers ---------- */
function resolveAssigneeLabel(assigneeIdOrText, staffList){
  if (!assigneeIdOrText) return "Unassigned";
  const idStr = String(assigneeIdOrText);
  const found = (staffList || []).find(s =>
    String(s.id) === idStr ||
    String((s.staffId || "")) === idStr ||
    (s.email && s.email === idStr) ||
    (s.name && s.name === idStr)
  );
  if (found) {
    const composed = (found.firstName ? found.firstName : "") + (found.lastName ? " " + found.lastName : "");
    return found.name || found.fullName || (composed.trim() || found.email) || "Unassigned";
  }
  return idStr.length > 16 ? (idStr.slice(0,8) + "…") : idStr;
}

/* ---------- LocalStorage keys ---------- */
const LS_MODE = "mudoCalMode";
const LS_CAT  = "mudoCalCategory";
const LS_ASG  = "mudoCalAssignee";

/* ---------- Component ---------- */
export default function CalendarView({
  events: eventsOverride,
  openEventId = null,
  onDeepLinkHandled,
}) {
  const safePlanner = (() => { try { return usePlanner(); } catch { return {}; } })();
  const ctxEvents = Array.isArray(safePlanner?.events) ? safePlanner.events : [];
  const upsertEvents = typeof safePlanner?.upsertEvents === "function" ? safePlanner.upsertEvents : (() => {});
  const { staffList = [] } = useStaff();
  const events = Array.isArray(eventsOverride) ? eventsOverride : ctxEvents;

  useEffect(() => {
    if (!openEventId) return;
    const match = (events || []).find((ev) => String(ev.id) === String(openEventId));
    if (match) {
      setEditing(match);
      onDeepLinkHandled?.(true);
    } else if (events?.length >= 0) {
      onDeepLinkHandled?.(false);
    }
  }, [openEventId, events, onDeepLinkHandled]);

  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(LS_MODE) || "week"; } catch { return "week"; }
  }); // day | week | month | year
  const [cursor, setCursor] = useState(()=> startOfDay(new Date()));
  const [editing, setEditing] = useState(null); // TaskEditorModal (edit existing or quick stub)
  const [creating, setCreating] = useState(false); // NewTaskModal
  const [seed, setSeed] = useState(null); // seed for NewTaskModal
  const [dragId, setDragId] = useState(null);   // event being dragged

  /* filters */
  const categories = useMemo(() => {
    const set = new Set();
    events.forEach(e => e.category && set.add(e.category));
    return ["all", ...Array.from(set).sort()];
  }, [events]);

  const assignees = useMemo(() => {
    const staffOptions = (staffList || []).map(s => ({
      id: String(s.id != null ? s.id : (s.staffId != null ? s.staffId : (s.email || s.name || ""))),
      label: (s.name || s.fullName || ((s.firstName || "") + " " + (s.lastName || "")).trim() || s.email || String(s.id)),
      color: s.color || "#888",
    }));
    const seen = new Set(staffOptions.map(o => o.id));
    const customs = [];
    events.forEach(e => {
      const raw = e.assigneeId != null ? e.assigneeId : e.assignee;
      if (!raw) return;
      const key = String(raw);
      if (!seen.has(key)) {
        customs.push({ id: key, label: resolveAssigneeLabel(raw, staffList), color: e.color || "#888" });
        seen.add(key);
      }
    });
    return [{ id: "all", label: "All Staff", color: "#777" }, ...staffOptions, ...customs];
  }, [events, staffList]);

  const [category, setCategory] = useState(() => {
    try { return localStorage.getItem(LS_CAT) || "all"; } catch { return "all"; }
  });
  const [assignee, setAssignee] = useState(() => {
    try { return localStorage.getItem(LS_ASG) || "all"; } catch { return "all"; }
  });

  useEffect(()=>{ try { localStorage.setItem(LS_MODE, mode); } catch {} }, [mode]);
  useEffect(()=>{ try { localStorage.setItem(LS_CAT, category); } catch {} }, [category]);
  useEffect(()=>{ try { localStorage.setItem(LS_ASG, assignee); } catch {} }, [assignee]);

  const filtered = useMemo(() => {
    return events.filter(e => {
      const eCat = (e.category ?? "").trim();
      const catOk = category === "all" || !eCat || eCat === category;
      const aIdRaw = e.assigneeId != null ? e.assigneeId : e.assignee;
      const aIdStr = aIdRaw != null ? String(aIdRaw) : "";
      const asgOk = assignee === "all" || !aIdStr || aIdStr === String(assignee);
      return catOk && asgOk;
    });
  }, [events, category, assignee]);

  /* Navigation */
  function goPrev(){
    if (mode==="day")   setCursor(addDays(cursor, -1));
    if (mode==="week")  setCursor(addDays(cursor, -7));
    if (mode==="month") setCursor(addMonths(cursor, -1));
    if (mode==="year")  setCursor(addYears(cursor, -1));
  }
  function goNext(){
    if (mode==="day")   setCursor(addDays(cursor, 1));
    if (mode==="week")  setCursor(addDays(cursor, 7));
    if (mode==="month") setCursor(addMonths(cursor, 1));
    if (mode==="year")  setCursor(addYears(cursor, 1));
  }
  function goToday(){ setCursor(startOfDay(new Date())); }

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT","TEXTAREA","SELECT"].includes(e.target?.tagName)) return;
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
      if (e.key.toLowerCase() === "t") goToday();
      if (e.key.toLowerCase() === "n") openNewForCursor();
      if (e.key.toLowerCase() === "w") setMode("week");
      if (e.key.toLowerCase() === "m") setMode("month");
      if (e.key.toLowerCase() === "d") setMode("day");
      if (e.key.toLowerCase() === "y") setMode("year");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, cursor, category, assignee]); // deps just to keep closure fresh

  /* Derived ranges */
  const dayFrom   = useMemo(()=> startOfDay(cursor), [cursor]);
  const dayTo     = useMemo(()=> endOfDay(cursor), [cursor]);
  const weekFrom  = useMemo(()=> startOfWeek(cursor), [cursor]);
  const weekTo    = useMemo(()=> endOfDay(addDays(startOfWeek(cursor), 6)), [cursor]);
  const monthFrom = useMemo(()=> startOfMonth(cursor), [cursor]);
  const monthTo   = useMemo(()=> endOfDay(new Date(cursor.getFullYear(), cursor.getMonth()+1, 0)), [cursor]);
  const yearFrom  = useMemo(()=> startOfYear(cursor), [cursor]);
  const yearTo    = useMemo(()=> endOfDay(new Date(cursor.getFullYear(), 11, 31)), [cursor]);

  /* Filtered events for each view */
  const dayEvents = useMemo(
    ()=> filtered.filter(e => overlaps(e, dayFrom, dayTo)).sort((a,b)=>new Date(a.start)-new Date(b.start)),
    [filtered, dayFrom, dayTo]
  );

  const weekEventsByDay = useMemo(()=>{
    const map = new Map();
    for (let i=0;i<7;i++){ const d=addDays(weekFrom,i); map.set(d.toDateString(), []); }
    for (const e of filtered) {
      for (let i=0;i<7;i++){
        const d=addDays(weekFrom,i);
        const from=startOfDay(d), to=endOfDay(d);
        if (overlaps(e, from, to)) map.get(d.toDateString()).push(e);
      }
    }
    for (const [,arr] of map) arr.sort((a,b)=>new Date(a.start)-new Date(b.start));
    return [...map.entries()];
  }, [filtered, weekFrom]);

  const monthGrid = useMemo(()=>{
    const first = startOfMonth(cursor);
    const start = startOfWeek(first);
    const cells = [];
    for (let i=0;i<42;i++){
      const d = addDays(start,i);
      const list = filtered
        .filter(e => overlaps(e, startOfDay(d), endOfDay(d)))
        .sort((a,b)=>new Date(a.start)-new Date(b.start));
      cells.push({ date: d, list, inMonth: d.getMonth() === cursor.getMonth() });
    }
    return cells;
  }, [filtered, cursor]);

  const yearMonths = useMemo(()=>{
    const arr = [];
    for (let m=0;m<12;m++){
      const base = new Date(cursor.getFullYear(), m, 1);
      const from = startOfMonth(base);
      const to   = endOfDay(new Date(base.getFullYear(), base.getMonth()+1, 0));
      const count = filtered.filter(e=> overlaps(e, from, to)).length;
      arr.push({ month: m, base, count });
    }
    return arr;
  }, [filtered, cursor]);

  /* Title */
  const title = useMemo(()=>{
    if (mode==="day")   return formatDate(dayFrom, { weekday:"long", month:"long", day:"numeric", year:"numeric" });
    if (mode==="week")  return `${formatDate(weekFrom, { month:"short", day:"numeric" })} – ${formatDate(weekTo, { month:"short", day:"numeric", year:"numeric" })}`;
    if (mode==="month") return formatDate(monthFrom, { month:"long", year:"numeric" });
    if (mode==="year")  return String(cursor.getFullYear());
    return "";
  }, [mode, dayFrom, weekFrom, weekTo, monthFrom, cursor]);

  /* drag-move to a day with simple collision hinting */
  const onDayDrop = (dateObj, e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const ev = events.find(x => String(x.id) === String(id));
    if (!ev) return;
    const moved = moveToDatePreserveTime(ev.start, ev.end, dateObj);

    // naive overlap check (same assignee and resource)
    const nextStart = new Date(moved.start).getTime();
    const nextEnd   = new Date(moved.end).getTime();
    const clashes = events.some(other => {
      if (String(other.id) === String(ev.id)) return false;
      const sameAsg = String(other.assigneeId ?? other.assignee ?? "") === String(ev.assigneeId ?? ev.assignee ?? "");
      const sameRes = String(other.resource || "") === String(ev.resource || "");
      if (!sameAsg && !sameRes) return false;
      const os = new Date(other.start).getTime();
      const oe = new Date(other.end).getTime();
      return oe >= nextStart && os <= nextEnd;
    });

    if (clashes) {
      if (!confirm("This move may overlap an existing task for the same assignee/resource. Continue?")) {
        setDragId(null);
        return;
      }
    }

    upsertEvents([{ ...ev, ...moved }]);
    setDragId(null);
  };

  /* quick add helpers */
  const openNewForDate = (dateObj) => {
    // Prefer full NewTaskModal (so it wires chain/assignee etc.)
    setSeed({
      title: "",
      start: new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 9, 0, 0, 0).toISOString(),
      end:   new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 10, 0, 0, 0).toISOString(),
      stage: "forming",
      qty: 1,
      category: "production",
    });
    setCreating(true);
  };
  const openNewForCursor = () => openNewForDate(cursor);

  return (
    <div className="space-y-3 mudo-cal">
      {/* THEME PATCHES */}
      <style>{`
        html:not(.dark) .mudo-cal select option { color:#0b1220; background:#fff; }
        html:not(.dark) .mudo-cal .bg-white\\/10 { background: rgba(0,0,0,.06) !important; }
        html:not(.dark) .mudo-cal .hover\\:bg-white\\/5:hover { background: rgba(0,0,0,.05) !important; }
        html:not(.dark) .mudo-cal .border-white\\/10 { border-color: rgba(11,18,32,.18) !important; }
        html:not(.dark) .mudo-cal .border-white\\/5  { border-color: rgba(11,18,32,.10) !important; }
        .dark .mudo-cal .cal-cell { border: 1px solid rgba(255,255,255,.10); border-radius: 12px; background: transparent; }
        html:not(.dark) .mudo-cal .cal-cell { border: 1px solid rgba(11,18,32,.18); border-radius: 12px; background: #fff; }
        html:not(.dark) .mudo-cal .cal-cell:hover { background:#f7f9fc; }
        .mudo-cal .ev-text { color:#fff; }
        html:not(.dark) .mudo-cal .ev-text { color:#0b1220; }
        html:not(.dark) .mudo-cal .day-num { color:#0b1220; }
        .dark .mudo-cal .day-num { color:rgba(255,255,255,.7); }
        .mudo-cal .drop-hint { outline: 2px dashed rgba(59,130,246,.5); outline-offset: 2px; }
        .mudo-cal .add-dot { width: 20px; height: 20px; line-height: 18px; text-align:center; border-radius: 9999px; border:1px solid rgba(255,255,255,.25); }
        html:not(.dark) .mudo-cal .add-dot { border-color: rgba(11,18,32,.18); }
      `}</style>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm font-semibold">{title}</div>

        <div className="flex items-center gap-1">
          <button className="px-2 py-1 rounded bg-white/10" onClick={goPrev} aria-label="Previous">‹</button>
          <button className="px-2 py-1 rounded bg-white/10" onClick={goToday}>Today</button>
          <button className="px-2 py-1 rounded bg-white/10" onClick={goNext} aria-label="Next">›</button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <select className="px-2 py-1 rounded bg-white/10 text-sm" value={category} onChange={(e)=>setCategory(e.target.value)}>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <select className="px-2 py-1 rounded bg-white/10 text-sm" value={assignee} onChange={(e)=>setAssignee(e.target.value)}>
            {assignees.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>

          {["day","week","month","year"].map(v=>(
            <button
              key={v}
              onClick={()=>setMode(v)}
              className={`px-3 py-1 rounded-full text-sm ${mode===v?"bg-white text-black":"bg-white/10"}`}
              title={`View ${v}`}
            >
              {v[0].toUpperCase()+v.slice(1)}
            </button>
          ))}

          {/* New Task button */}
          <button
            onClick={openNewForCursor}
            className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
            title="Add a task on the current date (N)"
          >
            + New Task
          </button>
        </div>
      </div>

      {/* Legend */}
      {assignee === "all" && (
        <div className="flex flex-wrap gap-2 text-xs">
          {assignees.filter(a => a.id!=="all").map(a => (
            <span key={a.id} className="inline-flex items-center gap-2 px-2 py-1 rounded border border-white/10">
              <span className="inline-block w-3 h-3 rounded" style={{ background: a.color || "#888" }} />
              {a.label}
            </span>
          ))}
        </div>
      )}

      {/* Body */}
      {mode==="day"   && <DayView   items={dayEvents}          onEdit={setEditing} staffList={staffList} onQuickAdd={(d)=>openNewForDate(d)} currentDate={dayFrom} />}
      {mode==="week"  && <WeekView  itemsByDay={weekEventsByDay} onEdit={setEditing} staffList={staffList} onDayDrop={onDayDrop} setDragId={setDragId} dragId={dragId} onQuickAdd={(d)=>openNewForDate(d)} cursor={cursor} />}
      {mode==="month" && <MonthView grid={monthGrid}           onEdit={setEditing} staffList={staffList} onDayDrop={onDayDrop} setDragId={setDragId} dragId={dragId} onQuickAdd={(d)=>openNewForDate(d)} cursor={cursor} />}
      {mode==="year"  && <YearView  months={yearMonths} year={cursor.getFullYear()} onJump={(m)=>setCursor(new Date(cursor.getFullYear(), m, 1))} />}

      {/* Modals */}
      <TaskEditorModal open={!!editing} event={editing || undefined} onClose={()=>setEditing(null)} />
      {creating && (
        <NewTaskModal
          isOpen={creating}
          onClose={() => { setCreating(false); setSeed(null); }}
          seed={seed || {}}
        />
      )}
    </div>
  );
}

/* ---------- Views ---------- */

function DayView({ items, onEdit, staffList, onQuickAdd, currentDate }) {
  const isToday = (d) => {
    const t = new Date();
    return d.getFullYear()===t.getFullYear() && d.getMonth()===t.getMonth() && d.getDate()===t.getDate();
  };
  return (
    <div className={`p-2 cal-cell ${isToday(currentDate) ? "ring-2 ring-emerald-400/60" : ""}`}>
      {items.length === 0 ? (
        <div className="p-6 text-center text-gray-400">
          No items today.
          <div className="mt-3">
            <button
              className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
              onClick={()=>onQuickAdd(currentDate)}
            >
              + Add Task
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(ev => <EventCard key={ev.id} ev={ev} onEdit={onEdit} staffList={staffList} />)}
        </div>
      )}
    </div>
  );
}

function WeekView({ itemsByDay, onEdit, staffList, onDayDrop, setDragId, dragId, onQuickAdd, cursor }) {
  const isSameDay = (a,b)=> a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
  const today = new Date();
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-3">
      {itemsByDay.map(([k, list]) => {
        const d = new Date(k);
        const label = formatDate(d, { weekday:"short", month:"short", day:"numeric" });
        const highlight = isSameDay(today, d);
        return (
          <div
            key={k}
            className={`rounded-xl p-2 cal-cell ${dragId ? "drop-hint" : ""} ${highlight ? "ring-2 ring-emerald-400/60" : ""} relative`}
            onDragOver={(e)=>e.preventDefault()}
            onDrop={(e)=>onDayDrop(d, e)}
            onDoubleClick={()=>onQuickAdd(d)}
          >
            <div className="flex items-center justify-between text-xs font-semibold mb-2 opacity-80">
              <span>{label}</span>
              <button
                className="add-dot hover:bg-white/10"
                title="Add task"
                onClick={(e)=>{ e.stopPropagation(); onQuickAdd(d); }}
              >
                +
              </button>
            </div>
            <div className="space-y-2">
              {list.length ? list.map(ev =>
                <EventTiny key={ev.id} ev={ev} onEdit={onEdit} staffList={staffList} setDragId={setDragId} />
              ) : <div className="text-xs opacity-40">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthView({ grid, onEdit, staffList, onDayDrop, setDragId, dragId, onQuickAdd, cursor }) {
  const header = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const isSameDay = (a,b)=> a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
  const today = new Date();
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-7 text-xs opacity-70 px-1">{header.map(h=> <div key={h} className="px-1 py-1">{h}</div>)}</div>
      <div className="grid grid-cols-7 gap-2">
        {grid.map((cell, i)=>{
          const highlight = isSameDay(today, cell.date);
          return (
            <div
              key={i}
              className={`min-h-[90px] rounded-xl p-1 cal-cell ${cell.inMonth?"":"opacity-60"} ${dragId ? "drop-hint" : ""} ${highlight ? "ring-2 ring-emerald-400/60" : ""} relative`}
              onDragOver={(e)=>e.preventDefault()}
              onDrop={(e)=>onDayDrop(cell.date, e)}
              onDoubleClick={()=>onQuickAdd(cell.date)}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] opacity-70 day-num">{cell.date.getDate()}</div>
                <button
                  className="add-dot text-[12px] leading-[18px] hover:bg-white/10"
                  title="Add task"
                  onClick={(e)=>{ e.stopPropagation(); onQuickAdd(cell.date); }}
                >
                  +
                </button>
              </div>
              <div className="space-y-1">
                {cell.list.slice(0,4).map(ev =>
                  <EventStripe key={ev.id} ev={ev} onEdit={onEdit} staffList={staffList} setDragId={setDragId} />
                )}
                {cell.list.length>4 && <div className="text-[11px] opacity-60">+{cell.list.length-4} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function YearView({ months, year, onJump }) {
  return (
    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
      {months.map(m=>(
        <button
          key={m.month}
          onClick={()=>onJump(m.month)}
          className="rounded-xl border border-white/10 p-3 text-left hover:bg-white/5"
        >
          <div className="text-sm font-semibold">{formatDate(new Date(year, m.month, 1), { month:"long" })}</div>
          <div className="text-xs opacity-70 mt-1">{m.count} items</div>
        </button>
      ))}
    </div>
  );
}

/* ---------- Event Cards ---------- */

function EventCard({ ev, onEdit, staffList }) {
  const assigneeName = resolveAssigneeLabel(ev.assigneeId != null ? ev.assigneeId : ev.assignee, staffList);
  const stage = stageTag(ev);
  return (
    <button onClick={()=>onEdit(ev)} className="w-full text-left rounded-xl border border-white/10 p-3 relative pl-4 hover:bg-white/5">
      <span className="absolute left-0 top-0 bottom-0 w-1 rounded-l" style={{ background: ev.color || "#888" }} />
      <div className="text-sm font-semibold" style={{ color: ev.color }}>{ev.title}</div>
      <div className="text-xs opacity-70">{timeRange(ev.start, ev.end)}</div>
      {(assigneeName || ev.category) && (
        <div className="text-[11px] opacity-80 mt-0.5">{assigneeName || "Unassigned"} · {ev.category || "—"}</div>
      )}
      <div className="text-[11px] mt-1">
        <span className="px-2 py-0.5 rounded" style={{ background: rgba(ev.color, .18), color: ev.color, border: `1px solid ${ev.color}` }}>
          {stage}
        </span>
        {ev.batch ? <span className="ml-2 opacity-70">Batch {ev.batch}</span> : null}
      </div>
    </button>
  );
}

function EventTiny({ ev, onEdit, staffList, setDragId }) {
  const assigneeName = resolveAssigneeLabel(ev.assigneeId != null ? ev.assigneeId : ev.assignee, staffList);
  return (
    <button
      onClick={()=>onEdit(ev)}
      draggable
      onDragStart={(e)=>{ setDragId(String(ev.id)); e.dataTransfer.setData("text/plain", String(ev.id)); }}
      onDragEnd={()=> setDragId(null)}
      className="w-full text-left rounded-lg border border-white/10 p-2 relative pl-3 hover:bg-white/5"
      title={ev.title}
    >
      <span className="absolute left-0 top-0 bottom-0 w-1 rounded-l" style={{ background: ev.color || "#888" }} />
      <div className="text-xs font-semibold truncate" style={{ color: ev.color }}>{ev.title}</div>
      <div className="text-[11px] opacity-70">{timeRange(ev.start, ev.end)}</div>
      {(assigneeName || ev.category) && (
        <div className="text-[10px] opacity-70 mt-0.5">
          {assigneeName || "Unassigned"} · {ev.category || "—"}
        </div>
      )}
    </button>
  );
}

function EventStripe({ ev, onEdit, staffList, setDragId }) {
  const assigneeName = resolveAssigneeLabel(ev.assigneeId != null ? ev.assigneeId : ev.assignee, staffList);
  const bg = ev.color || "#888";
  return (
    <button
      onClick={()=>onEdit(ev)}
      draggable
      onDragStart={(e)=>{ setDragId(String(ev.id)); e.dataTransfer.setData("text/plain", String(ev.id)); }}
      onDragEnd={()=> setDragId(null)}
      className="w-full h-5 rounded overflow-hidden flex items-center hover:opacity-90"
      title={ev.title}
      style={{ background: rgba(bg, 0.22), border: `1px solid ${bg}` }}
    >
      <span className="inline-block h-full w-1.5" style={{ background: bg }} />
      <span className="text-[11px] px-2 truncate ev-text">
        {ev.title} · {assigneeName}
      </span>
    </button>
  );
}
